import * as React from 'react';
import {Query} from 'react-apollo';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    GetUserAssignmentsForStage as GetUserAssignmentsForStageQuery,
    GetUserAssignmentsForStageVariables as GetUserAssignmentsForStageQueryVariables,
    StageLeadUserAssignmentData as StageLeadUserAssignmentDataFragment,
} from 'nutshell-graphql-types';

import GET_USER_ASSIGNMENTS_FOR_STAGE from '../../graphql/queries/get-user-assignments-for-stage.graphql';

import {SalesAutomationCardUserAssignment} from '../cards/sales-automation-card-user-assignment';

import {StageSingleItemContainer} from './stage-single-item-container';

type OwnProps = {
    parentId: string;
    hideCards: boolean;
};

type GraphQLProps = {
    isLoading: boolean;
    errorMessage: string | null | undefined;
    isAssigneeEnabled: boolean;
    defaultAssigneeName: string | null | undefined;
    shouldRoundRobin: boolean | null | undefined;
    specialCaseCount: number;
};

type Props = OwnProps & GraphQLProps;

export class StageCardsUserAssignmentsContainerComponent extends React.PureComponent<Props> {
    render() {
        const card = this.hasUserAssignment() ? (
            <SalesAutomationCardUserAssignment
                isAssigneeEnabled={this.props.isAssigneeEnabled}
                defaultAssigneeName={this.props.defaultAssigneeName}
                shouldRoundRobin={this.props.shouldRoundRobin}
                specialCaseCount={this.props.specialCaseCount}
                isHidden={this.props.hideCards}
                parentId={this.props.parentId}
            />
        ) : null;

        return (
            <StageSingleItemContainer
                type='userAssignments'
                isLoading={this.props.isLoading}
                hideAddCard={this.props.hideCards}
                errorMessage={this.props.errorMessage}
                parentId={this.props.parentId}
                item={card}
            />
        );
    }

    hasUserAssignment = () => {
        if (this.props.defaultAssigneeName) return true;

        if (this.props.specialCaseCount > 0) return true;

        return false;
    };
}

// Need to make an inexact type here, so we can spread into the functions and avoid losing type information
type AutomationData = Partial<
    StageLeadUserAssignmentDataFragment['editableStageLeadAssignmentAutomationData']
>;

function getIsAssigneeEnabled(editableStageLeadAssignmentAutomationData: AutomationData): boolean {
    const defaultAutomation =
        editableStageLeadAssignmentAutomationData.stageLeadAssignmentDefaultAutomation;
    if (!defaultAutomation || defaultAutomation.shouldUnassign) return true;

    const assignee = defaultAutomation.assignee;

    return (
        !assignee ||
        (assignee.__typename === 'User' && assignee.isEnabled) ||
        (assignee.__typename === 'Team' && !assignee.deletedTime)
    );
}

function getDefaultAssigneeName(
    editableStageLeadAssignmentAutomationData: AutomationData
): string | null | undefined {
    const defaultAutomation =
        editableStageLeadAssignmentAutomationData.stageLeadAssignmentDefaultAutomation;
    if (!defaultAutomation) return null;

    if (defaultAutomation.shouldUnassign) return 'Unassign lead';

    if (defaultAutomation.assignee) return defaultAutomation.assignee.name;

    return null;
}

function getDefaultShouldRoundRobin(editableStageLeadAssignmentAutomationData: AutomationData) {
    const defaultAutomation =
        editableStageLeadAssignmentAutomationData.stageLeadAssignmentDefaultAutomation;
    if (!defaultAutomation) return null;

    return defaultAutomation.shouldRoundRobin;
}

class UserAssignmentsQuery extends Query<
    GetUserAssignmentsForStageQuery,
    GetUserAssignmentsForStageQueryVariables
> {}

export const StageCardsUserAssignmentsContainer = (props: OwnProps) => (
    <UserAssignmentsQuery
        query={GET_USER_ASSIGNMENTS_FOR_STAGE}
        variables={{stageId: props.parentId}}
    >
        {({data, loading, error}) => {
            const stage = data ? data.stage : null;

            return (
                <StageCardsUserAssignmentsContainerComponent
                    {...props}
                    isLoading={loading}
                    // @ts-expect-error
                    errorMessage={safelyGetGraphQLErrorMessage(error)}
                    isAssigneeEnabled={
                        stage
                            ? getIsAssigneeEnabled({
                                  ...stage.editableStageLeadAssignmentAutomationData,
                              })
                            : true
                    }
                    defaultAssigneeName={
                        stage
                            ? getDefaultAssigneeName({
                                  ...stage.editableStageLeadAssignmentAutomationData,
                              })
                            : undefined
                    }
                    shouldRoundRobin={
                        stage
                            ? getDefaultShouldRoundRobin({
                                  ...stage.editableStageLeadAssignmentAutomationData,
                              })
                            : null
                    }
                    specialCaseCount={
                        stage
                            ? stage.editableStageLeadAssignmentAutomationData
                                  .stageLeadAssignmentAutomations.length
                            : 0
                    }
                />
            );
        }}
    </UserAssignmentsQuery>
);
